// src/components/modules/TAI/TAIExportacion.js

import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Form,
  DatePicker,
  Select,
  Radio,
  Button,
  Space,
  message,
  Spin,
  Card,
  Typography,
  Divider,
} from "antd";
import {
  DownloadOutlined,
  FileExcelOutlined,
  CarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";
import api from "../../../axiosConfig";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text, Title } = Typography;

const ESTADOS_ASIGNACION = [
  { value: "pendiente", label: "Pendiente" },
  { value: "asignado", label: "Asignado" },
  { value: "confirmado", label: "Confirmado" },
  { value: "ejecutado", label: "Ejecutado" },
  { value: "cancelado", label: "Cancelado" },
];

const TIPOS_TRAYECTO = [
  { value: "interveredal", label: "Interveredal" },
  { value: "intermunicipal", label: "Intermunicipal" },
  { value: "urbano", label: "Urbano" },
];

const ESTADOS_PLANILLA = [
  { value: "generada", label: "Generada" },
  { value: "enviada", label: "Enviada" },
  { value: "canjeada", label: "Canjeada" },
  { value: "usada", label: "Usada" },
  { value: "recibida", label: "Recibida" },
  { value: "facturada", label: "Facturada" },
  { value: "anulada", label: "Anulada" },
];

const TAIExportacion = ({
  open,
  onClose,
  transportadoras = [],
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tipoExportacion, setTipoExportacion] = useState("traslados");

  const handleExportar = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const params = new URLSearchParams();

      if (values.fechas && values.fechas.length === 2) {
        params.append("fechaInicio", values.fechas[0].format("YYYY-MM-DD"));
        params.append("fechaFin", values.fechas[1].format("YYYY-MM-DD"));
      }

      params.append("tipo", tipoExportacion);

      if (values.transportadora_id) {
        params.append("transportadora_id", values.transportadora_id);
      }

      if (tipoExportacion === "traslados") {
        if (values.estado_asignacion) {
          params.append("estado_asignacion", values.estado_asignacion);
        }
        if (values.trayecto_tipo) {
          params.append("trayecto_tipo", values.trayecto_tipo);
        }
      } else {
        if (values.estado_planilla) {
          params.append("estado", values.estado_planilla);
        }
      }

      const response = await api.get(`/tai/exportar?${params.toString()}`, {
        responseType: "blob",
      });

      // Crear enlace de descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const fechaInicio = values.fechas?.[0]?.format("YYYY-MM-DD") || "inicio";
      const fechaFin = values.fechas?.[1]?.format("YYYY-MM-DD") || "fin";
      const filename = tipoExportacion === "traslados"
        ? `traslados_tai_${fechaInicio}_a_${fechaFin}.xlsx`
        : `planillas_tai_${fechaInicio}_a_${fechaFin}.xlsx`;

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Archivo exportado exitosamente");
      onClose();
    } catch (error) {
      console.error("Error al exportar:", error);
      message.error("Error al exportar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleTipoChange = (e) => {
    setTipoExportacion(e.target.value);
    form.resetFields(["estado_asignacion", "trayecto_tipo", "estado_planilla"]);
  };

  return (
    <Modal
      title={
        <Space>
          <FileExcelOutlined style={{ color: "#52c41a" }} />
          <span>Exportar Datos TAI</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancelar
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={<DownloadOutlined />}
          loading={loading}
          onClick={handleExportar}
          style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
        >
          Exportar Excel
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            fechas: [dayjs().startOf("month"), dayjs().endOf("month")],
          }}
        >
          {/* Tipo de exportación */}
          <Form.Item label="Tipo de Exportación">
            <Radio.Group value={tipoExportacion} onChange={handleTipoChange}>
              <Radio.Button value="traslados">
                <Space>
                  <CarOutlined />
                  Traslados TAI
                </Space>
              </Radio.Button>
              <Radio.Button value="planillas">
                <Space>
                  <FileTextOutlined />
                  Planillas TAI
                </Space>
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Divider />

          {/* Filtros comunes */}
          <Card size="small" title="Filtros" style={{ marginBottom: 16 }}>
            <Form.Item
              name="fechas"
              label="Rango de Fechas"
              rules={[{ required: true, message: "Seleccione un rango de fechas" }]}
            >
              <RangePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder={["Fecha inicio", "Fecha fin"]}
              />
            </Form.Item>

            <Form.Item
              name="transportadora_id"
              label="Transportadora"
            >
              <Select
                placeholder="Todas las transportadoras"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {transportadoras.map((t) => (
                  <Option key={t.id} value={t.id}>
                    {t.nombre}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* Filtros específicos de traslados */}
            {tipoExportacion === "traslados" && (
              <>
                <Form.Item
                  name="estado_asignacion"
                  label="Estado de Asignación"
                >
                  <Select
                    placeholder="Todos los estados"
                    allowClear
                  >
                    {ESTADOS_ASIGNACION.map((e) => (
                      <Option key={e.value} value={e.value}>
                        {e.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="trayecto_tipo"
                  label="Tipo de Trayecto"
                >
                  <Select
                    placeholder="Todos los tipos"
                    allowClear
                  >
                    {TIPOS_TRAYECTO.map((t) => (
                      <Option key={t.value} value={t.value}>
                        {t.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}

            {/* Filtros específicos de planillas */}
            {tipoExportacion === "planillas" && (
              <Form.Item
                name="estado_planilla"
                label="Estado de Planilla"
              >
                <Select
                  placeholder="Todos los estados"
                  allowClear
                >
                  {ESTADOS_PLANILLA.map((e) => (
                    <Option key={e.value} value={e.value}>
                      {e.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Card>

          {/* Información del archivo */}
          <Card size="small" style={{ backgroundColor: "#f6ffed" }}>
            <Text type="secondary">
              <FileExcelOutlined style={{ marginRight: 8 }} />
              El archivo se descargará en formato Excel (.xlsx) con todos los campos disponibles.
            </Text>
          </Card>
        </Form>
      </Spin>
    </Modal>
  );
};

TAIExportacion.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  transportadoras: PropTypes.array,
};

export default TAIExportacion;
